import { useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { CrmCommercialContact, CrmContactEntity, CommercialContactRole, SocialNetwork } from '@/services/api';
import { inputClass, labelClass } from './styles';

const ROLE_LABEL: Record<CommercialContactRole, string> = {
  prescriptor: 'Prescriptor',
  direccion: 'Dirección',
  tejedor: 'Tejedor de redes',
};
const SOCIAL: { key: SocialNetwork; label: string }[] = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'x', label: 'X' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'tiktok', label: 'TikTok' },
];

function Section({ title, hint, children, defaultOpen = false }: {
  title: string; hint?: string; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-4 border-t border-gray-200 pt-3">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full flex items-center justify-between text-left">
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        <span className="text-xs text-gray-400">{open ? '▲' : '▼'}</span>
      </button>
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

/** Alta básica de la entidad (parte que puede rellenar el lead) y contacto comercial (solo equipo). */
export default function LeadEntityFields({ entity, commercialContacts, onEntityChange, onCommercialChange }: {
  entity: CrmContactEntity;
  commercialContacts: CrmCommercialContact[];
  onEntityChange: (e: CrmContactEntity) => void;
  onCommercialChange: (c: CrmCommercialContact[]) => void;
}) {
  const branding = entity.branding || {};
  const pub = entity.publicContact || {};
  const social = pub.social || {};
  const setBranding = (patch: Partial<NonNullable<CrmContactEntity['branding']>>) =>
    onEntityChange({ ...entity, branding: { ...branding, ...patch } });
  const setPub = (patch: Partial<NonNullable<CrmContactEntity['publicContact']>>) =>
    onEntityChange({ ...entity, publicContact: { ...pub, ...patch } });

  const updateContact = (i: number, patch: Partial<CrmCommercialContact>) =>
    onCommercialChange(commercialContacts.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  return (
    <>
      <Section title="Datos de la entidad" hint="Alta básica: el lead también los puede rellenar desde su enlace.">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className={labelClass}>Denominación legal</label>
            <input className={inputClass} value={entity.legalName || ''} onChange={(e) => onEntityChange({ ...entity, legalName: e.target.value })} /></div>
          <div><label className={labelClass}>Forma jurídica</label>
            <input className={inputClass} value={entity.legalForm || ''} onChange={(e) => onEntityChange({ ...entity, legalForm: e.target.value })} /></div>
          <div><label className={labelClass}>Nº de registro</label>
            <input className={inputClass} value={entity.registryNumber || ''} onChange={(e) => onEntityChange({ ...entity, registryNumber: e.target.value })} /></div>
          <div className="col-span-2"><label className={labelClass}>Logotipo (URL)</label>
            <input className={inputClass} placeholder="https://" value={branding.logoUrl || ''} onChange={(e) => setBranding({ logoUrl: e.target.value })} /></div>
          <div><label className={labelClass}>Isotipo (URL)</label>
            <input className={inputClass} placeholder="https://" value={branding.iconUrl || ''} onChange={(e) => setBranding({ iconUrl: e.target.value })} /></div>
          <div><label className={labelClass}>Color principal</label>
            <div className="flex gap-2">
              <input type="color" aria-label="Elegir color principal" className="h-9 w-12 rounded border border-gray-300"
                value={branding.primaryColor || '#000000'} onChange={(e) => setBranding({ primaryColor: e.target.value })} />
              <input className={inputClass} placeholder="#rrggbb" value={branding.primaryColor || ''} onChange={(e) => setBranding({ primaryColor: e.target.value })} />
            </div>
          </div>
          <div className="col-span-2 text-xs font-semibold text-gray-500 uppercase mt-2">Contacto público</div>
          <div className="col-span-2"><label className={labelClass}>Dirección</label>
            <input className={inputClass} value={pub.address || ''} onChange={(e) => setPub({ address: e.target.value })} /></div>
          <div><label className={labelClass}>Web</label>
            <input className={inputClass} placeholder="https://" value={pub.website || ''} onChange={(e) => setPub({ website: e.target.value })} /></div>
          <div><label className={labelClass}>Email</label>
            <input className={inputClass} value={pub.email || ''} onChange={(e) => setPub({ email: e.target.value })} /></div>
          <div><label className={labelClass}>Teléfono</label>
            <input className={inputClass} value={pub.phone || ''} onChange={(e) => setPub({ phone: e.target.value })} /></div>
          {SOCIAL.map((n) => (
            <div key={n.key}><label className={labelClass}>{n.label}</label>
              <input className={inputClass} placeholder="https://" value={social[n.key] || ''}
                onChange={(e) => setPub({ social: { ...social, [n.key]: e.target.value } })} /></div>
          ))}
        </div>
      </Section>

      <Section title={`Contacto comercial (${commercialContacts.length})`} hint="Solo lo ve nuestro equipo; nunca se envía al lead.">
        <div className="space-y-3">
          {commercialContacts.map((c, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 border border-gray-200 rounded-lg p-3">
              <div><label className={labelClass}>Papel</label>
                <select className={inputClass} value={c.role} onChange={(e) => updateContact(i, { role: e.target.value as CommercialContactRole })}>
                  {(Object.keys(ROLE_LABEL) as CommercialContactRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select></div>
              <div><label className={labelClass}>Nombre</label>
                <input className={inputClass} value={c.name || ''} onChange={(e) => updateContact(i, { name: e.target.value })} /></div>
              <div><label className={labelClass}>Teléfono</label>
                <input className={inputClass} value={c.phone || ''} onChange={(e) => updateContact(i, { phone: e.target.value })} /></div>
              <div><label className={labelClass}>Email</label>
                <input className={inputClass} value={c.email || ''} onChange={(e) => updateContact(i, { email: e.target.value })} /></div>
              <div className="col-span-2"><label className={labelClass}>Comentarios internos</label>
                <textarea className={inputClass} rows={2} value={c.internalNotes || ''} onChange={(e) => updateContact(i, { internalNotes: e.target.value })} /></div>
              <div className="col-span-2 flex justify-end">
                <button type="button" onClick={() => onCommercialChange(commercialContacts.filter((_, j) => j !== i))}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600">
                  <TrashIcon className="w-4 h-4" /> Quitar
                </button>
              </div>
            </div>
          ))}
          {commercialContacts.length < 10 && (
            <button type="button" onClick={() => onCommercialChange([...commercialContacts, { role: 'prescriptor' }])}
              className="inline-flex items-center gap-1 text-sm font-medium text-vida-700 hover:bg-vida-50 px-2 py-1 rounded">
              <PlusIcon className="w-4 h-4" /> Añadir persona
            </button>
          )}
        </div>
      </Section>
    </>
  );
}
